/**
 * Session-first start for the installed immersive Quest app (utils/questApp).
 *
 * Inside the immersive app the page is never composited before a WebXR
 * session exists: window rAF runs at 0 fps, ResizeObserver never reports, the
 * R3F <Canvas> stays 300x150 and never creates its root, so
 * `xrStore.enterVR()` throws "not connected to three.js", and the store waits
 * for the canvas while the canvas waits for the session.
 *
 * Meta's guidance (https://developers.meta.com/horizon/documentation/web/pwa-webxr/):
 * call `navigator.xr.requestSession('immersive-vr')` right after load, guarded
 * by `window.getDigitalGoodsService`; the app-icon tap counts as the user
 * activation. So:
 *
 *  1. startQuestImmersiveApp() (src/index.tsx, before React renders) requests
 *     the session at once on world pages, and again on `sessiongranted`
 *     (same-origin navigation while immersive: portal / world hops reload the
 *     page with location.replace).
 *  2. <QuestImmersiveBridge/> (inside <XR>) hands the session to three.js with
 *     `gl.xr.setSession(session)` - the same call @react-three/xr's enterVR
 *     makes. The XR store binds through the WebXRManager 'sessionstart'
 *     event, so controllers, hands, XROrigin and VRViewer's subscription work
 *     exactly as after a normal Enter VR.
 *  3. When the session ends: Quit closes the app (window.close); any other
 *     end shows a re-entry screen (tap anywhere = new session), never a blank
 *     frozen page.
 *
 * Off the immersive app every export here is a no-op.
 */
import { isQuestImmersiveApp, isWorldPath, markQuestLaunch, questReturnPath, QUEST_SESSION_INIT } from './questApp';

type Renderer = { xr: {
  setSession: (s: XRSession) => Promise<void>; getSession: () => XRSession | null; isPresenting: boolean;
  addEventListener: (type: string, l: () => void) => void; removeEventListener: (type: string, l: () => void) => void;
} };

let session: XRSession | null = null;
let requesting: Promise<XRSession | null> | null = null;
let renderer: Renderer | null = null;
let quitting = false;
let overlay: HTMLDivElement | null = null;
const listeners = new Set<() => void>();
const log = (...a: unknown[]) => console.info('[quest-app]', ...a);

const notify = () => listeners.forEach((l) => { try { l(); } catch { /* listener */ } });
export const onQuestSessionChange = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
export const getQuestSession = () => session;

// A session the XR store started itself (not ours) ending must also re-offer
// entry, so watch the renderer's sessions too.
let rendererSession: XRSession | null = null;
const onRendererStart = () => { rendererSession = renderer?.xr.getSession() ?? null; };
const onRendererEnd = () => {
  const ended = rendererSession;
  rendererSession = null;
  if (ended && ended !== ownSession) onEnd();
};
let ownSession: XRSession | null = null;

/** The renderer is up (<QuestImmersiveBridge/>): hand any pending session over. */
export function attachQuestRenderer(r: Renderer | null) {
  if (renderer && renderer !== r) {
    renderer.xr.removeEventListener('sessionstart', onRendererStart);
    renderer.xr.removeEventListener('sessionend', onRendererEnd);
  }
  renderer = r;
  if (!r) return;
  r.xr.addEventListener('sessionstart', onRendererStart);
  r.xr.addEventListener('sessionend', onRendererEnd);
  if (r.xr.isPresenting) onRendererStart();
  void handOff();
}

async function setHighFrameRate(s: XRSession) {
  // Same as @react-three/xr's default frameRate: 'high'.
  const rates = (s as any).supportedFrameRates as Float32Array | undefined;
  if (!rates || !rates.length || typeof (s as any).updateTargetFrameRate !== 'function') return;
  try { await (s as any).updateTargetFrameRate(rates[rates.length - 1]); } catch { /* not supported */ }
}

let handingOff = false;
async function handOff() {
  const s = session, r = renderer;
  if (!s || !r || handingOff) return;
  if (r.xr.getSession() === s) return;
  if (r.xr.isPresenting) {
    // The XR store started its own session first (e.g. its sessiongranted
    // listener won the race): keep that one and drop ours.
    log('renderer already presenting; ending the early session');
    try { await s.end(); } catch { /* already ended */ }
    return;
  }
  handingOff = true;
  try {
    // Never await the frame rate before setSession: on Quest the
    // updateTargetFrameRate promise only settles once frames are flowing, and
    // none flow until three.js owns the session (it hung on the loading
    // screen). @react-three/xr also runs the two side by side.
    void setHighFrameRate(s);
    await r.xr.setSession(s);
    log('session handed to three.js');
    // Voice: same as VRViewer's enterVR (join muted, retry audio playback).
    // Both queue until the voice component mounts; audio that still needs a
    // gesture is retried by the VR menu on mount and on its mic button.
    const voice = await import('../stores/voiceStore');
    voice.requestVoiceJoin({ muted: true });
    voice.unlockVoiceAudio();
  } catch (err) {
    console.warn('[quest-app] handing the session to three.js failed', err);
  } finally {
    handingOff = false;
  }
}

function onEnd() {
  log('session ended', quitting ? '(quit)' : '');
  session = null;
  notify();
  if (quitting) {
    quitting = false;
    try { window.close(); } catch { /* not allowed */ }
    // Still here (the TWA ignored close): offer the way back in.
    window.setTimeout(() => showReentry('GathrXR is paused'), 500);
    return;
  }
  showReentry('You left VR');
}

/** Request immersive-vr (once at a time) and hand it to three.js when ready. */
export function requestQuestSession(reason: string): Promise<XRSession | null> {
  if (session) return Promise.resolve(session);
  if (requesting) return requesting;
  const xr = (navigator as any).xr as XRSystem | undefined;
  if (!xr) return Promise.resolve(null);
  log('requesting immersive-vr:', reason);
  requesting = xr.requestSession('immersive-vr', QUEST_SESSION_INIT)
    .then((s) => {
      log('session granted', renderer ? '(renderer ready)' : '(waiting for renderer)');
      session = s;
      ownSession = s;
      s.addEventListener('end', onEnd, { once: true });
      hideReentry();
      notify();
      void handOff();
      return s;
    })
    .catch((err) => {
      // NotAllowedError = no user activation (e.g. a reload without
      // sessiongranted); InvalidStateError = the XR store already has one.
      log('requestSession failed:', err?.name || err, err?.message || '');
      if (!(renderer?.xr.isPresenting)) showReentry('Tap to enter VR');
      return null;
    })
    .finally(() => { requesting = null; });
  return requesting;
}

/** Quit button: end the session and close the app. */
export function quitQuestApp() {
  quitting = true;
  const s = session || renderer?.xr.getSession();
  if (s) void s.end().catch(() => onEnd());
  else onEnd();
}

// ---- re-entry screen (only visible if the headset shows the 2D page at all) ----
function showReentry(title: string) {
  if (typeof document === 'undefined' || !isWorldPath(location.pathname)) return;
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.setAttribute('role', 'dialog');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:2147483000;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;background:radial-gradient(ellipse at 50% 35%,#0c1626 0%,#060e18 100%);color:#eaf6f8;font-family:Outfit,sans-serif;text-align:center;padding:16px';
    overlay.innerHTML = '<div style="font-size:30px;font-weight:800">Gathr<span style="color:#00c2b3">XR</span></div>'
      + '<div data-title style="font-size:16px;opacity:.7"></div>'
      + '<button data-enter style="padding:16px 44px;font-size:18px;font-weight:700;color:#fff;background:linear-gradient(135deg,#00c2b3,#0077b6);border:none;border-radius:999px;cursor:pointer;font-family:inherit">Enter VR</button>'
      + '<button data-quit style="background:none;border:none;color:rgba(234,246,248,.5);font-size:14px;cursor:pointer;text-decoration:underline;font-family:inherit">Close app</button>';
    overlay.querySelector('[data-quit]')!.addEventListener('click', (e) => { e.stopPropagation(); try { window.close(); } catch { /* ignored */ } });
    // Any tap or key is a user activation: spend it on a new session.
    overlay.addEventListener('click', () => { void requestQuestSession('re-entry tap'); });
    document.body.appendChild(overlay);
    window.addEventListener('keydown', onReentryKey);
  }
  (overlay.querySelector('[data-title]') as HTMLElement).textContent = title;
  overlay.style.display = 'flex';
}
function onReentryKey() { if (overlay?.style.display !== 'none') void requestQuestSession('re-entry key'); }
function hideReentry() { if (overlay) overlay.style.display = 'none'; }

let started = false;
/**
 * Call once, as early as possible (src/index.tsx, before React). Marks the
 * launch, sends non-world pages back to a world, and requests the session.
 */
export function startQuestImmersiveApp() {
  if (started || typeof window === 'undefined') return;
  started = true;
  if (!markQuestLaunch() || !isQuestImmersiveApp()) return;
  log('immersive Quest app detected', location.pathname + location.search);

  // Same-origin navigation while immersive (world hops): the browser grants a
  // session to the new page.
  (navigator as any).xr?.addEventListener?.('sessiongranted', () => { void requestQuestSession('sessiongranted'); });

  if (!isWorldPath(location.pathname)) {
    // A 2D page (dashboard, login, welcome) can't be seen in the headset. Let
    // Supabase finish reading any auth redirect first, then go to the world.
    void import('../config/supabase')
      .then(({ supabase }) => Promise.race([supabase.auth.getSession(), new Promise((r) => setTimeout(r, 3000))]))
      .catch(() => null)
      .finally(() => { log('not a world page; returning to', questReturnPath()); location.replace(questReturnPath()); });
    return;
  }
  void (async () => {
    try {
      const ok = await (navigator as any).xr.isSessionSupported('immersive-vr');
      if (!ok) { log('immersive-vr not supported'); return; }
    } catch { /* ask anyway */ }
    void requestQuestSession('launch');
  })();
}

/**
 * ResizeObserver stand-in for the R3F <Canvas> in the immersive app
 * (`<Canvas resize={{ polyfill: QuestResizeObserver }}>`). Real
 * ResizeObservers report during rendering steps, which never run before the
 * session exists, so the canvas would never get a size and R3F would never
 * create its root. This one reports right away and then once a second
 * (react-use-measure's callback just re-reads getBoundingClientRect, which
 * forces layout and works without compositing).
 */
export class QuestResizeObserver {
  private timers = new Map<Element, number>();
  constructor(private cb: (entries: unknown[], observer: unknown) => void) {}
  observe(el: Element) {
    if (this.timers.has(el)) return;
    window.setTimeout(() => this.cb([], this), 0);
    this.timers.set(el, window.setInterval(() => this.cb([], this), 1000));
  }
  unobserve(el: Element) { const t = this.timers.get(el); if (t) window.clearInterval(t); this.timers.delete(el); }
  disconnect() { this.timers.forEach((t) => window.clearInterval(t)); this.timers.clear(); }
}
